import React from "react";

import { dollarDisplay } from "EmployerApplication/CostEstimationUtils";
import { FieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";

import { MobileTable } from "../CartEmployeePriceBreakdown/CartEmployeePriceBreakdownTable";
import { Table, TH, TD, TR, HeaderTR, FooterTR, FooterTD, SpacerTR, SnugTD, NestedTR } from "Cobalt/TableV2";
import { getCart } from "Common/CartService";
import { CONTRIBUTION_TYPES } from "Common/Constants";
import { SIFinalFormField, SIFormSelect } from "Common/forms";
import { SizeSwitch } from "Common/Responsive";
import SICheckbox from "Common/SICheckbox";

import makeBem from "Utils/makeBem";

const bem = makeBem("EditEmployeePriceBreakdown");

const DesktopRow = ({
  row,
  name,
  index,
  medicalPlans,
  showTierSelector,
  showPlanSelector,
  showDentalPlan,
  showVisionPlan,
  handleEnrollingToggle,
  contributionOptions,
  contributionType,
  showFullTime,
  fullTimeEmployees,
}) => {
  if (!row) return <></>;
  const dependents = row.dependents || [];
  const dependentTotals =
    contributionType == CONTRIBUTION_TYPES.fixed || medicalPlans[0].pricing_type == "segment"
      ? {
          companyCost: row.dependentsCompanyCost,
          employeeCost: row.dependentsEmployeeCost,
          premium: row.dependentsPremium,
        }
      : undefined;
  return (
    <>
      <TR>
        {showFullTime && (
          <SnugTD>
            <SICheckbox
              className={bem("otherCheckbox")}
              name={`${name}.full_time`}
              checked={fullTimeEmployees[index]}
              onChange={() => handleEnrollingToggle("fullTime", index)}
            />
          </SnugTD>
        )}

        <SnugTD>
          <SICheckbox
            className={bem("medicalCheckbox")}
            name={`${name}.is_enrolling`}
            checked={row.is_enrolling}
            onChange={() => handleEnrollingToggle("medical", index)}
          />
        </SnugTD>
        {showDentalPlan && (
          <SnugTD>
            <SICheckbox
              className={bem("otherCheckbox")}
              name={`${name}.is_enrolling_dental`}
              checked={row.is_enrolling_dental}
              onChange={() => handleEnrollingToggle("dental", index)}
            />
          </SnugTD>
        )}

        {showVisionPlan && (
          <SnugTD>
            <SICheckbox
              className={bem("otherCheckbox")}
              name={`${name}.is_enrolling_vision`}
              checked={row.is_enrolling_vision}
              onChange={() => handleEnrollingToggle("vision", index)}
            />
          </SnugTD>
        )}

        <TD>{row.name}</TD>
        <TD>{row.age}</TD>
        {showTierSelector && (
          <TD>
            <SIFinalFormField
              name={`${name}.role`}
              component={SIFormSelect}
              componentProps={{
                placeholder: "--------",
                options: contributionOptions,
              }}
              mb={0}
              pl={0}
            />
          </TD>
        )}

        {showPlanSelector && (
          <TD>
            <SIFinalFormField
              name={`${name}.selected_medical_plan`}
              component={SIFormSelect}
              componentProps={{
                options: medicalPlans.reduce((acc, medicalPlan) => {
                  acc[medicalPlan.display_name] = medicalPlan.id;
                  return acc;
                }, {}),
              }}
              mb={0}
              pl={0}
            />
          </TD>
        )}

        <TD align="right">{dollarDisplay(row.employeeCost || null)}</TD>
        <TD align="right">{dollarDisplay(row.companyCost || null)}</TD>
        <TD align="right">{dollarDisplay(row.premium || null)}</TD>
      </TR>
      {dependentTotals && dependents.length > 0 && (
        <>
          <NestedTR>
            {showFullTime && <TD />}
            <TD />
            {showDentalPlan && <TD />}
            {showVisionPlan && <TD />}
            <TD colspan={2}>Dependent Costs</TD>
            {showTierSelector && <TD />}
            {showPlanSelector && <TD />}
            <TD align="right">{dollarDisplay(dependentTotals.employeeCost || null)}</TD>
            <TD align="right">{dollarDisplay(dependentTotals.companyCost || null)}</TD>
            <TD align="right">{dollarDisplay(dependentTotals.premium || null)}</TD>
          </NestedTR>
          <SpacerTR />
        </>
      )}

      <FieldArray name={`${name}.dependents`}>
        {({ fields }) =>
          fields.map((dependentName, dependentIndex) =>
            dependents[dependentIndex] ? (
              <React.Fragment key={dependentIndex}>
                <NestedTR>
                  {showFullTime && <TD />}
                  <TD>
                    <SICheckbox
                      name={`${dependentName}.is_enrolling`}
                      checked={dependents[dependentIndex].is_enrolling}
                      onChange={() => handleEnrollingToggle("medical", index, dependentIndex)}
                    />
                  </TD>
                  {showDentalPlan && (
                    <TD>
                      <SICheckbox
                        name={`${dependentName}.is_enrolling_dental`}
                        checked={dependents[dependentIndex].is_enrolling_dental}
                        onChange={() => handleEnrollingToggle("dental", index, dependentIndex)}
                      />
                    </TD>
                  )}

                  {showVisionPlan && (
                    <TD>
                      <SICheckbox
                        name={`${dependentName}.is_enrolling_vision`}
                        checked={dependents[dependentIndex].is_enrolling_vision}
                        onChange={() => handleEnrollingToggle("vision", index, dependentIndex)}
                      />
                    </TD>
                  )}

                  <TD>{dependents[dependentIndex].name}</TD>
                  <TD>{dependents[dependentIndex].age}</TD>
                  {showTierSelector && <TD />}
                  {showPlanSelector && <TD />}
                  {!dependentTotals ? (
                    <>
                      <TD align="right">{dollarDisplay(dependents[dependentIndex].employeeCost || null)}</TD>
                      <TD align="right">{dollarDisplay(dependents[dependentIndex].companyCost || null)}</TD>
                      <TD align="right">{dollarDisplay(dependents[dependentIndex].premium || null)}</TD>
                    </>
                  ) : (
                    <TD colspan={3} />
                  )}
                </NestedTR>
                <SpacerTR />
              </React.Fragment>
            ) : (
              <></>
            ),
          )
        }
      </FieldArray>
    </>
  );
};

const DesktopTable = ({
  rows,
  totals,
  medicalPlans,
  showTierSelector,
  showPlanSelector,
  showDentalPlan,
  showVisionPlan,
  handleEnrollingToggle,
  contributionOptions,
  contributionType,
  showFullTime,
  fullTimeEmployees,
}) => (
  <Table>
    <thead>
      <HeaderTR>
        {showFullTime && <TH>Full Time?</TH>}
        <TH>Medical?</TH>
        {showDentalPlan && <TH>Dental?</TH>}
        {showVisionPlan && <TH>Vision?</TH>}
        <TH>Employee</TH>
        <TH>Age</TH>
        {showTierSelector && <TH>Tier</TH>}
        {showPlanSelector && <TH>Plan</TH>}
        <TH align="right">Employee Share</TH>
        <TH align="right">Company Share</TH>
        <TH align="right">Total Cost</TH>
      </HeaderTR>
    </thead>
    <tbody className={bem()}>
      <FieldArray name="employees">
        {({ fields }) =>
          fields.map((name, index) => (
            <DesktopRow
              key={index}
              row={fields.value[index]}
              dependentTotals={rows[index]?.dependentTotals}
              name={name}
              index={index}
              medicalPlans={medicalPlans}
              showTierSelector={showTierSelector}
              showPlanSelector={showPlanSelector}
              showDentalPlan={showDentalPlan}
              showVisionPlan={showVisionPlan}
              handleEnrollingToggle={handleEnrollingToggle}
              contributionOptions={contributionOptions}
              contributionType={contributionType}
              showFullTime={showFullTime}
              fullTimeEmployees={fullTimeEmployees}
            />
          ))
        }
      </FieldArray>
    </tbody>
    <tfoot>
      <FooterTR>
        {showFullTime && <FooterTD />}
        <FooterTD />
        {showDentalPlan && <FooterTD />}
        {showVisionPlan && <FooterTD />}
        <FooterTD />
        <FooterTD />
        {showTierSelector && <FooterTD />}
        {showPlanSelector && <FooterTD />}
        <FooterTD align="right">{dollarDisplay(totals.totalEmployeeCost.toFixed(2))}</FooterTD>
        <FooterTD align="right">{dollarDisplay(totals.totalCompanyCost.toFixed(2))}</FooterTD>
        <FooterTD align="right">{dollarDisplay(totals.totalPremium.toFixed(2))}</FooterTD>
      </FooterTR>
    </tfoot>
  </Table>
);

export const EditEmployeePriceBreakdownTable = ({
  rows,
  totals,
  medicalPlans,
  dentalPlan,
  visionPlan,
  handleEnrollingToggle,
  contributionType,
  contributionOptions,
  contributions,
  fullTimeEmployees,
}) => {
  const showTierSelector = contributions.length > 1;
  const showPlanSelector = medicalPlans.length > 1;
  const showDentalPlan = !!dentalPlan;
  const showVisionPlan = !!visionPlan;
  const { long_term_disability: longTermDisability, short_term_disability: shortTermDisability, life } = useSelector(getCart);

  const showFullTime = longTermDisability || shortTermDisability || life;
  return (
    <SizeSwitch
      xs={
        <MobileTable
          employees={rows}
          totals={totals}
          medicalPlans={medicalPlans}
          showTierSelector={showTierSelector}
          showPlanSelector={showPlanSelector}
          showDentalPlan={showDentalPlan}
          showVisionPlan={showVisionPlan}
          handleEnrollingToggle={handleEnrollingToggle}
          contributionOptions={contributionOptions}
          contributionType={contributionType}
        />
      }
      md={
        <DesktopTable
          rows={rows}
          totals={totals}
          medicalPlans={medicalPlans}
          showTierSelector={showTierSelector}
          showPlanSelector={showPlanSelector}
          showDentalPlan={showDentalPlan}
          showVisionPlan={showVisionPlan}
          handleEnrollingToggle={handleEnrollingToggle}
          contributionOptions={contributionOptions}
          contributionType={contributionType}
          showFullTime={showFullTime}
          fullTimeEmployees={fullTimeEmployees}
        />
      }
    />
  );
};

export default EditEmployeePriceBreakdownTable;
